import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Description,
  DateRange,
  Person,
  Email,
  Groups,
  Cloud,
  Delete,
  Add,
  Assessment,
  ExpandMore
} from '@mui/icons-material';

import { AssessmentMetadata, CloudAccount, CloudEnvironment } from '../types';
import { loadQuestions, AssessmentData } from '../services/questionService';
import './IntroForm.css';

interface IntroFormProps {
  onSubmit: (metadata: AssessmentMetadata) => void;
  initialData?: AssessmentMetadata;
}

const cloudProviders = [
  'AWS',
  'Azure',
  'Google Cloud',
  'Oracle Cloud',
  'IBM Cloud',
  'Alibaba Cloud',
  'Other'
];

const defaultMetadata: AssessmentMetadata = {
  teamName: 'Cloud Platform Team',
  contactName: 'John Doe',
  contactEmail: 'john.doe@example.com',
  description: 'This assessment covers our cloud operations across all business units to identify areas for FinOps improvement.',
  date: new Date().toISOString().slice(0, 10),
  cloudAccounts: [
    {
      id: Date.now().toString(),
      name: 'Production',
      provider: 'AWS',
      accountId: '123456789012',
      environment: 'Development',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ]
};

// Separate handlers for different input types
interface NewAccountChangeHandlers {
  name: (e: React.ChangeEvent<HTMLInputElement>) => void;
  provider: (e: SelectChangeEvent) => void;
  accountId: (e: React.ChangeEvent<HTMLInputElement>) => void;
  environment: (e: SelectChangeEvent) => void;
}

const IntroForm: React.FC<IntroFormProps> = ({ onSubmit, initialData = defaultMetadata }) => {
  const [metadata, setMetadata] = useState<AssessmentMetadata>(initialData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAccount, setNewAccount] = useState<CloudAccount>({
    id: Date.now().toString(),
    name: '',
    provider: 'AWS',
    accountId: '',
    environment: 'Development',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssessmentData = async () => {
      try {
        setLoading(true);
        const data = await loadQuestions();
        console.log('Loaded assessment data:', data);
        setAssessmentData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load assessment data:', err);
        setError('Failed to load assessment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentData();
    const savedMetadataJson = localStorage.getItem('metadata');
    if (savedMetadataJson) {
      setMetadata(JSON.parse(savedMetadataJson));
    }
  }, []);

  const handleInputChange = (field: keyof AssessmentMetadata) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMetadata({
      ...metadata,
      [field]: e.target.value
    });
  };

  const handleAddAccountClick = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewAccount({
      id: Date.now().toString(),
      name: '',
      provider: 'AWS',
      accountId: '',
      environment: 'Development',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  };

  // Create specific handlers for each input type
  const accountChangeHandlers: NewAccountChangeHandlers = {
    name: (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewAccount({
        ...newAccount,
        name: e.target.value
      });
    },
    provider: (e: SelectChangeEvent) => {
      setNewAccount({
        ...newAccount,
        provider: e.target.value
      });
    },
    accountId: (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewAccount({
        ...newAccount,
        accountId: e.target.value
      });
    },
    environment: (e: SelectChangeEvent) => {
      setNewAccount({
        ...newAccount,
        environment: e.target.value as CloudEnvironment
      });
    }
  };

  const handleAddAccount = () => {
    setMetadata({
      ...metadata,
      cloudAccounts: [...metadata.cloudAccounts, newAccount]
    });
    handleCloseDialog();
  };

  const handleDeleteAccount = (id: string) => {
    setMetadata({
      ...metadata,
      cloudAccounts: metadata.cloudAccounts.filter(account => account.id !== id)
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    console.log('WA DEBUG - metadata: ', metadata);
    localStorage.setItem('metadata', JSON.stringify(metadata));

    e.preventDefault();
    onSubmit(metadata);
  };

  const handleReset = () => {
    setMetadata(defaultMetadata);
    localStorage.setItem('metadata', JSON.stringify(metadata));
  };

  const isUsingDefaultData = () => {
    return (
      metadata.teamName === defaultMetadata.teamName &&
      metadata.contactName === defaultMetadata.contactName &&
      metadata.contactEmail === defaultMetadata.contactEmail
    );
  };

  return (
    <Box className="intro-form-container">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" align="center">
          {error}
        </Typography>
      ) : (
        <Card className="intro-form-card">
          <CardContent className="intro-form-content">
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="h4" className="intro-form-title">
                {assessmentData?.name || "Assessment"}
              </Typography>

              {assessmentData?.welcome_message && (
                <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
                  {assessmentData.welcome_message}
                </Typography>
              )}

              <Box className="intro-form-field">
                <TextField
                  fullWidth
                  label="Team Name"
                  value={metadata.teamName}
                  onChange={handleInputChange('teamName')}
                  required
                  InputProps={{
                    startAdornment: (
                      <Groups sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />
              </Box>

              <Box className="intro-form-field">
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={metadata.contactName}
                  onChange={handleInputChange('contactName')}
                  required
                  InputProps={{
                    startAdornment: (
                      <Person sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />
              </Box>

              <Box className="intro-form-field">
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={metadata.contactEmail}
                  onChange={handleInputChange('contactEmail')}
                  required
                  InputProps={{
                    startAdornment: (
                      <Email sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />
              </Box>

              <Box className="intro-form-field">
                <TextField
                  fullWidth
                  label="Assessment Date"
                  type="date"
                  value={metadata.date}
                  onChange={handleInputChange('date')}
                  required
                  InputProps={{
                    startAdornment: (
                      <DateRange sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />
              </Box>

              <Box className="intro-form-field">
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={metadata.description}
                  onChange={handleInputChange('description')}
                  required
                  InputProps={{
                    startAdornment: (
                      <Description sx={{ mr: 1, mt: 1.5, color: 'action.active' }} />
                    ),
                  }}
                />
              </Box>

              <Box className="intro-form-accounts">
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="cloud-accounts-content"
                    id="cloud-accounts-header"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Cloud sx={{ color: 'action.active' }} />
                      <Typography variant="h6">
                        Cloud Accounts (Optional)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddAccountClick}
                      >
                        Add Account
                      </Button>
                    </Box>

                    <List>
                      {metadata.cloudAccounts.map((account) => (
                        <ListItem
                          key={account.id}
                          sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemIcon>
                            <Cloud />
                          </ListItemIcon>
                          <ListItemText
                            primary={account.name}
                            secondary={`${account.provider} - ${account.accountId} (${account.environment})`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDeleteAccount(account.id)}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box className="intro-form-buttons">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={!isUsingDefaultData()}
                >
                  Reset to Default
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Assessment />}
                >
                  Start Assessment
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Add Cloud Account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Account Name"
            fullWidth
            value={newAccount.name}
            onChange={accountChangeHandlers.name}
            required
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Cloud Provider</InputLabel>
            <Select
              value={newAccount.provider}
              onChange={accountChangeHandlers.provider}
              label="Cloud Provider"
            >
              {cloudProviders.map((provider) => (
                <MenuItem key={provider} value={provider}>
                  {provider}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Account ID"
            fullWidth
            value={newAccount.accountId}
            onChange={accountChangeHandlers.accountId}
            required
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Environment</InputLabel>
            <Select
              value={newAccount.environment}
              onChange={accountChangeHandlers.environment}
              label="Environment"
            >
              <MenuItem value="Development">Development</MenuItem>
              <MenuItem value="Staging">Staging</MenuItem>
              <MenuItem value="Production">Production</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleAddAccount} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntroForm; 